import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { fetchDiscover, fetchTrending, type StoryDetail } from './lib/api/content';
import { AuthProvider, fallbackContentLang } from './lib/auth';
import { initI18n } from './lib/i18n';
import { registerWebMcpTools } from './lib/webmcp';
import { router } from './router';
import './styles/fonts.css';
import './styles/tokens.css';
import './styles/app.css';

const qc = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 2 * 60_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

// Keşfet ilk açılışta en sık sayfa. Kenar fonksiyonu (functions/index.ts)
// veriyi HTML'e gömdüyse doğrudan kullan; yoksa React çizimini beklemeden iste.
declare global {
  interface Window {
    __BRIFING_DISCOVER__?: { lang: string; data: Awaited<ReturnType<typeof fetchDiscover>> };
    __BRIFING_STORY__?: { id: string; lang: string; data: StoryDetail };
  }
}
if (location.pathname === '/') {
  const lang = fallbackContentLang();
  const seeded = window.__BRIFING_DISCOVER__;
  if (seeded && seeded.lang === lang) qc.setQueryData(['discover', lang], seeded.data);
  else void qc.prefetchQuery({ queryKey: ['discover', lang], queryFn: () => fetchDiscover(lang) });
  void qc.prefetchQuery({ queryKey: ['trending', lang], queryFn: () => fetchTrending(lang, 8) });
}

// WebMCP: destekleyen tarayıcılarda ajanlara arama/okuma/gezinme araçları (bkz. lib/webmcp.ts).
// i18n'i beklemez: araçlar dil sözlüğüne bağlı değil, sayfa açılır açılmaz görünsünler.
registerWebMcpTools((to) => router.navigate(to), fallbackContentLang);

// Hikâye sayfası: kenar fonksiyonu (functions/s/[id].ts) hikâyeyi sunucuda
// çizip verisini de gömdü. Dil eşleşirse önbelleğe koy: React sunucu HTML'ini
// iskelet göstermeden aynı içerikle devralır. Eşleşmezse SPA her zamanki gibi çeker.
{
  const seed = window.__BRIFING_STORY__;
  if (seed && location.pathname === `/s/${seed.id}` && seed.lang === fallbackContentLang()) {
    qc.setQueryData(['story', seed.id, seed.lang], seed.data);
  }
}

await initI18n();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>
);
